import { theme } from '@resistapp/client/components/shared/theme';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useTrendChartContext } from '@resistapp/client/contexts/use-overview-context/trendchart-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import type { OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import {
  ColorScalePoint,
  getDynamicRiskScoreLegendConfig,
  getMetricAndLevel,
} from '@resistapp/client/utils/metric-utils';
import { MetricMode } from '@resistapp/common/types';
import { LinearGradient } from '@visx/gradient';
import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';
import { positioning, strokeColor } from '../chart-styles';
import { useOverviewReferenceLevels } from '../hooks/use-overview-reference-level';
import { getGradientScale } from '../scales';
import { BaseChart } from './base-chart';

export function RiskScoreChart() {
  const { queryFilters } = useSampleDataContext();
  const { getGroup, allAssays } = useAssayContext();
  const {
    graphHeight,
    trenchartTooltip: { trendChartSize, TooltipComponentForRiskScore },
  } = useTrendChartContext();
  const { activeOverviewConfiguration, activeAbundanceMode, effectiveSiteProcessMode, trendData, defaultProcessMode } =
    useOverviewContext();

  // Use effective process mode for consistency with risk calculation elsewhere
  const modeToUse = trendData?.length === 1 ? effectiveSiteProcessMode : defaultProcessMode;

  // Get the reference level (e.g., European mean) using our dedicated hook
  const referenceLevels = useOverviewReferenceLevels(MetricMode.RISK);

  // Get dynamic legend configuration based on trend data risk scores and reference level
  const { colorScaleConfig, gradientMultiplier, isSwOnlyScale } = getDynamicRiskScoreLegendConfig(
    trendData?.flat(), // Flatten trendData for the utility function
    activeOverviewConfiguration.mapLegendTicks || [],
    queryFilters.filters.selectedTargets, // Pass selected targets
    modeToUse, // Pass appropriate process mode
    activeAbundanceMode,
    getGroup,
    allAssays,
    referenceLevels,
  );

  const indexScale = useMemo(() => {
    const domain: [number, number] = isSwOnlyScale
      ? [0, 10] // Use 0-10 domain if only SW data
      : [activeOverviewConfiguration.detailBarGraphMin, activeOverviewConfiguration.detailBarGraphMax()]; // Otherwise use default domain
    return getGradientScale(graphHeight, domain);
  }, [graphHeight, activeOverviewConfiguration, isSwOnlyScale]);

  const getValue = useCallback(
    (datum: OverviewDatum) => {
      const [metric] = getMetricAndLevel(
        datum,
        queryFilters.filters.selectedTargets,
        MetricMode.RISK,
        modeToUse,
        activeAbundanceMode,
        getGroup,
        allAssays,
      );
      return isNil(metric) ? undefined : indexScale(metric);
    },
    [queryFilters.filters.selectedTargets, getGroup, indexScale, activeAbundanceMode, modeToUse, allAssays],
  );

  // Memoize the Legend component with its dynamic props
  const MemoizedGradientLegend = useCallback(
    (props: Omit<GradientLegendProps, 'colorScaleConfig' | 'gradientMultiplier'>) => (
      <GradientLegend {...props} colorScaleConfig={colorScaleConfig} gradientMultiplier={gradientMultiplier} />
    ),
    [colorScaleConfig, gradientMultiplier],
  );

  // Render gray reference lines for even numbers for the 0-10 SW scale
  const renderSwReferenceLines = useCallback(() => {
    const x2 = trendChartSize.width - positioning.margin.right - positioning.margin.left;
    return (
      <>
        {Array.from({ length: 5 }, (_, i) => 2 * (i + 1)).map(value => (
          <line key={value} x1="0" x2={x2} y1={indexScale(value)} y2={indexScale(value)} stroke={strokeColor} />
        ))}
      </>
    );
  }, [trendChartSize.width, indexScale]);

  return (
    <BaseChart
      width={trendChartSize.width}
      height={trendChartSize.height}
      indexScale={indexScale}
      labelScale={indexScale}
      Legend={MemoizedGradientLegend} // Use the memoized legend
      getValue={getValue}
      TooltipComponent={TooltipComponentForRiskScore}
      showData={{ quartileRange: false }}
      renderCustomReferenceLines={isSwOnlyScale ? renderSwReferenceLines : undefined}
    />
  );
}

interface GradientLegendProps {
  left: number;
  height: number;
  width: number;
  colorScaleConfig: ColorScalePoint[];
  gradientMultiplier: number;
}

function GradientLegend({ left, height, width, colorScaleConfig, gradientMultiplier }: GradientLegendProps) {
  const cornerRadius = theme.borders.radius.small;

  return (
    <svg width={width} height={height} style={{ position: 'absolute', left }}>
      <defs>
        <clipPath id="rounded-corner">
          <rect
            x={0}
            y={positioning.margin.top / 2}
            width={width}
            height={height - positioning.margin.top - positioning.margin.bottom}
            rx={cornerRadius}
          />
        </clipPath>
        <LinearGradient id="risk-score-gradient" x1="0%" y1="100%" x2="0%" y2="0%">
          {colorScaleConfig.map(point => (
            <stop key={point.value} offset={`${point.value * gradientMultiplier}%`} stopColor={point.color} />
          ))}
        </LinearGradient>
      </defs>
      <rect
        x={0}
        y={positioning.margin.top / 2}
        width={width}
        height={height - positioning.margin.top - positioning.margin.bottom}
        fill="url(#risk-score-gradient)"
        clipPath="url(#rounded-corner)"
      />
    </svg>
  );
}
